import React, { FC } from 'react'
import {
  Modal as GenericModal,
  ModalBody,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
  useDisclosure,
  UseDisclosureReturn,
  ThemingProps,
} from '@chakra-ui/react'
import { useColor } from '../../theme'

interface IModalProps {
  body: React.JSX.Element
  footer?: FC<{ close: VoidFunction }> | React.JSX.Element
  header?: React.JSX.Element
  wrapper?: FC<{ children?: React.ReactNode }>
  isCentered?: boolean
  isOpen: boolean
  size?: ThemingProps<'Modal'>['size']
  onClose: () => void
}

export interface IModal {
  ModalComponent: FC
  close: UseDisclosureReturn['onClose']
  isOpen: UseDisclosureReturn['isOpen']
  open: UseDisclosureReturn['onOpen']
  toggle: UseDisclosureReturn['onToggle']
}

/**
 * @deprecated Please use the `Dialog` component instead
 */
export const Modal: FC<IModalProps> = ({
  onClose,
  isOpen,
  header,
  body,
  footer,
  wrapper: Wrapper,
  isCentered = false,
  size,
}) => {
  const gray = useColor({ nuance: 5, palette: 'secondary' })
  const content = (
    <>
      {header && <ModalHeader>{header}</ModalHeader>}
      <ModalBody color={gray} fontWeight="medium">
        {body}
      </ModalBody>
      {footer && typeof footer !== 'function' ? (
        <ModalFooter>{footer}</ModalFooter>
      ) : (
        footer && <ModalFooter>{footer({ close })}</ModalFooter>
      )}
    </>
  )

  return (
    <GenericModal
      closeOnOverlayClick={false}
      isCentered={isCentered}
      isOpen={isOpen}
      onClose={onClose}
      size={size}>
      <ModalOverlay />
      <ModalContent data-testid="modal-content">
        {Wrapper ? <Wrapper>{content}</Wrapper> : content}
      </ModalContent>
    </GenericModal>
  )
}

/**
 * useModal severely hurts performance by recreating a new component on every render
 * @deprecated Please use the `Dialog` component instead and refactor to `Dialog` whenever you come across it
 */
export function useModal({
  body,
  footer: Footer,
  header,
  isCentered,
  wrapper,
  size,
}: Omit<IModalProps, 'isOpen' | 'onClose'>): IModal {
  const { isOpen, onClose, onOpen, onToggle } = useDisclosure()

  const ModalComponent = () => (
    <Modal
      body={body}
      footer={
        typeof Footer !== 'function' ? Footer : <Footer close={onClose} />
      }
      header={header}
      isCentered={isCentered}
      isOpen={isOpen}
      onClose={onClose}
      size={size}
      wrapper={wrapper}
    />
  )

  return {
    ModalComponent,
    close: onClose,
    isOpen,
    open: onOpen,
    toggle: onToggle,
  }
}
